import { reaction } from 'mobx';
import type { WidgetInitParams } from '@spro-front/widget';

import { apiUrls } from 'shared/entities/domain';
import { IRootStore } from 'shared/entities/store/rootStore';
import type { ISmartbotSupportWidgetStore } from 'shared/entities/store/smartbotSupportWidgetStore';
import {
  WIDGET_AUTH_UNAVAILABLE_ERROR_CODES,
  openWidgetAuthorizationError,
  WidgetState
} from 'shared/entities/widgetSupport';
import type { ManagerWidgetAuthTokenServer } from 'shared/entities/widgetSupport';
import {
  injectWidgetScript,
  removeWidgetScript
} from 'shared/utils/widgetScriptInjection';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';

const WIDGET_INIT_TIMEOUT_MS = 10000;
const WIDGET_READY_TIMEOUT_MS = 10000;
const WIDGET_READY_POLL_INTERVAL_MS = 100;

// IS_PROD означает production-сборку, а не только настоящее production-окружение:
// удалённые feature/dev/stage/prod-образы собираются в production-режиме.
// На localhost manager widget выключен и включается только явным флагом.
const MANAGER_SUPPORT_WIDGET_ENABLED =
  process.env.NODE_ENV === 'test' ||
  String(process.env.IS_PROD) === 'true' ||
  process.env.ENABLE_MANAGER_SUPPORT_WIDGET === 'true';

const isWidgetLifecycleStopped = (state: WidgetState): boolean =>
  state === WidgetState.paused || state === WidgetState.disposed;

/**
 * Координатор жизненного цикла support-виджета: следит за identity менеджера,
 * переинициализирует виджет в manager- или анонимном режиме и временно
 * переключает его в режим тестирования.
 *
 * Намеренно без `makeObservable`: собственного observable-состояния у стора нет, UI из него
 * ничего не рендерит. Единственный источник реактивности — `reaction` на `userStore.user.id`;
 * остальные поля приватные и нужны только чтобы отсекать устаревшие переходы.
 */
export class SmartbotSupportWidgetStore
  extends ReactionsHandlerStore
  implements ISmartbotSupportWidgetStore
{
  readonly rootStore: IRootStore;

  /**
   * `_requestedManagerId` — режим, который нужен сейчас по состоянию кабинета.
   * `_activeManagerId` — менеджер, для которого виджет фактически авторизован.
   * Они могут временно отличаться, пока выполняется переход.
   *
   * Ревизия привязывает асинхронный переход к конкретному состоянию store:
   * результат старого перехода нельзя применять после logout, смены пользователя
   * или удаления store.
   */
  private _requestedManagerId: string | null = null;
  private _lifecycleRevision = 0;
  private _synchronizedRevision = 0;
  private _transitionPromise: Promise<void> | null = null;
  private _transitionAbortController: AbortController | null = null;
  private _activeManagerId: string | null = null;
  private _supportApiKey: string | null = null;
  private _widgetState = WidgetState.idle;
  private _testingWidgetRevision = 0;
  private _isTestingWidgetInjecting = false;
  private _isTestingWidgetInjected = false;

  constructor(rootStore: IRootStore) {
    super();

    this.rootStore = rootStore;
  }

  private handleManagerIdChange = (managerId: string | null): void => {
    // Реакция только фиксирует новое желаемое состояние. Сам переход выполняется
    // отдельно, чтобы не запускать несколько переключений виджета параллельно.
    this._requestedManagerId = managerId;
    this._lifecycleRevision += 1;
    this._transitionAbortController?.abort();
    this.scheduleAuthorizationSync();
  };

  initialize = (): void => {
    if (!MANAGER_SUPPORT_WIDGET_ENABLED) {
      return;
    }

    if (this._widgetState === WidgetState.disposed) {
      this._widgetState = WidgetState.idle;
    }

    if (this.reactions.has('authorization')) {
      return;
    }

    // `fireImmediately` запускает первый переход сразу после создания store,
    // а проверка выше делает повторный вызов initialize безопасным.
    this.addReaction({
      key: 'authorization',
      reaction: reaction(
        () => this.rootStore.userStore.user?.id ?? null,
        this.handleManagerIdChange,
        { fireImmediately: true }
      )
    });
  };

  pauseManagerWidget = (): void => {
    if (this._widgetState === WidgetState.paused) {
      return;
    }

    // При удалении manager-виджета глобальная конфигурация window.smartbot
    // может исчезнуть. Сохраняем API-ключ до очистки, чтобы потом восстановить
    // анонимный виджет и повторно запросить manager-токен.
    this._supportApiKey =
      this._supportApiKey ?? window.smartbot?.[0]?.apiKey ?? null;
    this._widgetState = WidgetState.paused;
    this._lifecycleRevision += 1;
    this._transitionAbortController?.abort();
    window.smartbotWidgetRemove?.({ clearSessionToken: true });
    this._activeManagerId = null;
    this._synchronizedRevision = 0;
  };

  resumeManagerWidget = async (): Promise<void> => {
    if (this._widgetState !== WidgetState.paused) {
      return;
    }

    // Сначала возвращаем сам анонимный виджет, затем отдельным переходом
    // синхронизируем его с текущим авторизованным менеджером.
    this._widgetState = WidgetState.restoring;
    this._lifecycleRevision += 1;
    this._synchronizedRevision = 0;
    const revision = this._lifecycleRevision;

    await this.restoreAndSyncManagerWidget(revision);
  };

  startTestingWidget = async (apiKey: string): Promise<boolean> => {
    if (this._isTestingWidgetInjecting || this._isTestingWidgetInjected) {
      return false;
    }

    const revision = this._testingWidgetRevision + 1;
    this._testingWidgetRevision = revision;
    this._isTestingWidgetInjecting = true;
    this.pauseManagerWidget();

    try {
      await injectWidgetScript(apiKey, {
        clearSessionToken: true
      });

      if (revision !== this._testingWidgetRevision) {
        return false;
      }

      this._isTestingWidgetInjected = true;

      return true;
    } catch (error) {
      if (revision !== this._testingWidgetRevision) {
        return false;
      }

      removeWidgetScript({ clearSessionToken: true });
      this._isTestingWidgetInjected = false;
      void this.resumeManagerWidget();

      throw error;
    } finally {
      if (revision === this._testingWidgetRevision) {
        this._isTestingWidgetInjecting = false;
      }
    }
  };

  stopTestingWidget = async (): Promise<void> => {
    if (!this._isTestingWidgetInjecting && !this._isTestingWidgetInjected) {
      return;
    }

    this._testingWidgetRevision += 1;
    this._isTestingWidgetInjecting = false;
    this._isTestingWidgetInjected = false;
    removeWidgetScript({ clearSessionToken: true });
    await this.resumeManagerWidget();
  };

  disposeAll = (): void => {
    super.disposeAll();
    this._lifecycleRevision += 1;
    this._transitionAbortController?.abort();
    if (this._isTestingWidgetInjecting || this._isTestingWidgetInjected) {
      this._testingWidgetRevision += 1;
      this._isTestingWidgetInjecting = false;
      this._isTestingWidgetInjected = false;
      removeWidgetScript({ clearSessionToken: true });
    }
    if (this._activeManagerId || this._requestedManagerId) {
      window.smartbotWidgetRemove?.({ clearSessionToken: true });
    }
    this._requestedManagerId = null;
    this._synchronizedRevision = this._lifecycleRevision;
    this._transitionPromise = null;
    this._transitionAbortController = null;
    this._activeManagerId = null;
    this._supportApiKey = null;
    this._widgetState = WidgetState.disposed;
  };

  private scheduleAuthorizationSync = (): void => {
    if (
      this._widgetState === WidgetState.paused ||
      this._widgetState === WidgetState.restoring ||
      this._widgetState === WidgetState.disposed ||
      this._transitionPromise
    ) {
      return;
    }

    // В каждый момент времени допускается один переход. Если во время него
    // изменилась ревизия, finally запустит синхронизацию с актуальным состоянием
    // вместо создания конкурирующего перехода.
    const transition = this.syncAuthorization();
    const trackedTransition = transition.finally(() => {
      if (this._transitionPromise !== trackedTransition) {
        return;
      }

      this._transitionPromise = null;

      if (
        this._widgetState !== WidgetState.paused &&
        this._widgetState !== WidgetState.restoring &&
        this._widgetState !== WidgetState.disposed &&
        this._synchronizedRevision !== this._lifecycleRevision
      ) {
        this.scheduleAuthorizationSync();
      }
    });
    this._transitionPromise = trackedTransition;
  };

  private async syncAuthorization(): Promise<void> {
    if (
      this._widgetState === WidgetState.paused ||
      this._widgetState === WidgetState.restoring ||
      this._widgetState === WidgetState.disposed
    ) {
      return;
    }

    // Сохраняем снимок пары managerId/revision. Все await ниже должны завершить
    // именно этот переход; проверки ревизии не дают старому ответу перезаписать
    // виджет после logout или смены пользователя.
    const managerId = this._requestedManagerId;
    const revision = this._lifecycleRevision;

    // Реакция может прийти повторно с тем же значением. Если эта ревизия уже
    // синхронизирована, повторно удалять и создавать iframe не нужно.
    if (this._synchronizedRevision === revision) {
      return;
    }

    const abortController = new AbortController();
    this._transitionAbortController = abortController;

    if (managerId) {
      // Если identity поменялась, сначала удаляем авторизацию предыдущего
      // менеджера и только после этого включаем режим нового менеджера.
      if (this._activeManagerId && this._activeManagerId !== managerId) {
        await this.disableManagerMode(false, abortController.signal);
      }
      await this.enableManagerMode(managerId, revision, abortController.signal);
    } else if (
      this._widgetState === WidgetState.manager ||
      this._activeManagerId
    ) {
      // Отсутствие user.id означает anonymous-режим. При logout manager-сессия
      // очищается, а обычный анонимный виджет восстанавливается.
      await this.disableManagerMode(true, abortController.signal);
    }

    if (this._transitionAbortController === abortController) {
      this._transitionAbortController = null;
    }

    if (this._lifecycleRevision === revision) {
      this._synchronizedRevision = revision;
    }
  }

  private async enableManagerMode(
    managerId: string,
    revision: number,
    signal: AbortSignal
  ): Promise<void> {
    if (this._activeManagerId === managerId) {
      return;
    }

    if (signal.aborted || this._requestedManagerId !== managerId) {
      return;
    }

    // Inject-скрипт загружается независимо от кабинета. Сначала убеждаемся,
    // что он создал публичный API виджета, и только после этого запрашиваем
    // manager-токен и вызываем его init.
    const widgetReady = await this.waitForWidget(signal);

    if (
      !widgetReady ||
      signal.aborted ||
      this._requestedManagerId !== managerId ||
      this._lifecycleRevision !== revision
    ) {
      return;
    }

    const { apiKey, widgetInit } = widgetReady;

    const response = await this.rootStore.networkStore.api<
      ManagerWidgetAuthTokenServer,
      (typeof WIDGET_AUTH_UNAVAILABLE_ERROR_CODES)[number]
    >(apiUrls.AUTH_OBTAIN_SUPPORT_WIDGET_AUTH_TOKEN, {
      method: 'POST',
      config: { signal },
      /**
       * Support-виджет настроен только в проде: на dev и stage канала нет и backend
       * штатно отвечает channel_not_found. Стор в этом случае просто остаётся
       * в анонимном режиме, поэтому тост и запись в Sentry здесь не нужны.
       */
      expectedErrorCodes: WIDGET_AUTH_UNAVAILABLE_ERROR_CODES,
      showExpectedError: false,
      showUnexpectedError: false
    });

    if (
      signal.aborted ||
      this._requestedManagerId !== managerId ||
      this._lifecycleRevision !== revision
    ) {
      return;
    }

    if (response.isError || !response.data?.manager_auth_token) {
      // Ошибка авторизации manager-режима не должна оставлять пользователя
      // без support-виджета: сам виджет продолжает работать анонимно.
      this._widgetState = WidgetState.anonymous;

      if (
        !response.isError ||
        !WIDGET_AUTH_UNAVAILABLE_ERROR_CODES.some(
          (errorCode) => errorCode === response.data?.code
        )
      ) {
        this.showAuthorizationError();
      }

      return;
    }

    this._activeManagerId = managerId;

    const initialized = await this.initializeWidget(
      widgetInit,
      {
        apiKey,
        managerAuthToken: response.data.manager_auth_token
      },
      signal
    );

    if (initialized) {
      this._widgetState = WidgetState.manager;
    }

    if (!initialized && this._activeManagerId === managerId) {
      // Manager init мог завершиться ошибкой или быть прерван. В актуальном
      // переходе удаляем неуспешный вариант и возвращаем anonymous-режим.
      this._activeManagerId = null;

      if (
        !signal.aborted &&
        this._requestedManagerId === managerId &&
        this._lifecycleRevision === revision
      ) {
        window.smartbotWidgetRemove?.({ clearSessionToken: true });
        const anonymousInitialized = await this.initializeWidget(
          widgetInit,
          { apiKey },
          signal
        );

        if (anonymousInitialized) {
          this._widgetState = WidgetState.anonymous;
        }

        if (
          !signal.aborted &&
          this._requestedManagerId === managerId &&
          this._lifecycleRevision === revision
        ) {
          this.showAuthorizationError();
        }
      }
    }
  }

  private showAuthorizationError = (): void => {
    openWidgetAuthorizationError(
      this.rootStore.appNotificationsStore,
      this.retryAuthorization
    );
  };

  private retryAuthorization = (): void => {
    if (
      !this._requestedManagerId ||
      this._widgetState === WidgetState.paused ||
      this._widgetState === WidgetState.restoring ||
      this._widgetState === WidgetState.disposed
    ) {
      return;
    }

    // Retry — это новая ревизия того же желаемого manager-перехода. Уже идущий
    // запрос отменяется, чтобы его поздний ответ не завершил старую попытку.
    this._lifecycleRevision += 1;
    const revision = this._lifecycleRevision;
    this._transitionAbortController?.abort();

    if (this._supportApiKey && !window.smartbotWidgetInit) {
      void this.restoreAndSyncManagerWidget(revision);

      return;
    }

    this.scheduleAuthorizationSync();
  };

  private restoreAndSyncManagerWidget = async (
    revision: number
  ): Promise<void> => {
    // Восстановление inject-скрипта и manager-авторизация разделены: скрипт
    // может быть удалён в режиме тестирования, а managerId при этом сохраняется.
    const restored = await this.restoreSupportWidget(revision);

    if (
      restored &&
      this._widgetState !== WidgetState.paused &&
      this._widgetState !== WidgetState.restoring &&
      this._lifecycleRevision === revision
    ) {
      this.scheduleAuthorizationSync();
    }
  };

  private restoreSupportWidget = async (revision: number): Promise<boolean> => {
    if (!this._supportApiKey) {
      if (this._widgetState === WidgetState.restoring) {
        this._widgetState = WidgetState.idle;
      }

      return true;
    }

    // injectWidgetScript заново создаёт script/config globals и anonymous-сессию.
    // После этого syncAuthorization при необходимости переключит её в manager-режим.
    this._widgetState = WidgetState.restoring;

    try {
      await injectWidgetScript(this._supportApiKey);

      if (
        this._widgetState === WidgetState.restoring &&
        this._lifecycleRevision === revision
      ) {
        this._widgetState = WidgetState.anonymous;
      }

      return true;
    } catch {
      if (
        !isWidgetLifecycleStopped(this._widgetState) &&
        this._lifecycleRevision === revision
      ) {
        this.showAuthorizationError();
      }

      return false;
    } finally {
      if (this._widgetState === WidgetState.restoring) {
        this._widgetState = WidgetState.idle;
      }

      if (
        this._lifecycleRevision !== revision &&
        !isWidgetLifecycleStopped(this._widgetState)
      ) {
        this.scheduleAuthorizationSync();
      }
    }
  };

  private async disableManagerMode(
    restoreAnonymous: boolean,
    signal: AbortSignal
  ): Promise<void> {
    // `clearSessionToken` удаляет JWT-сессию, но не публичный API-ключ. Ключ
    // нужен для повторной инициализации anonymous-виджета после logout или
    // неудачной manager-авторизации.
    const apiKey = this._supportApiKey;
    const widgetInit = window.smartbotWidgetInit;

    window.smartbotWidgetRemove?.({ clearSessionToken: true });
    this._activeManagerId = null;
    this._widgetState = WidgetState.idle;

    if (restoreAnonymous && apiKey && widgetInit && !signal.aborted) {
      const initialized = await this.initializeWidget(
        widgetInit,
        { apiKey },
        signal
      );

      if (initialized) {
        this._widgetState = WidgetState.anonymous;
      }
    }
  }

  private async waitForWidget(signal: AbortSignal): Promise<{
    apiKey: string;
    widgetInit: NonNullable<typeof window.smartbotWidgetInit>;
  } | null> {
    if (!window.smartbot && !this._supportApiKey) {
      return null;
    }

    // Inject-скрипт загружается независимо от кабинета, поэтому нужен bounded
    // wait, а не немедленная ошибка. Abort останавливает ожидание, если начался
    // другой lifecycle-переход.
    const startedAt = Date.now();

    while (Date.now() - startedAt < WIDGET_READY_TIMEOUT_MS) {
      if (signal.aborted) {
        return null;
      }

      const apiKey = this._supportApiKey ?? window.smartbot?.[0]?.apiKey;
      const widgetInit = window.smartbotWidgetInit;

      if (apiKey && widgetInit) {
        this._supportApiKey = apiKey;

        return { apiKey, widgetInit };
      }

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, WIDGET_READY_POLL_INTERVAL_MS);
      });
    }

    return null;
  }

  private initializeWidget = async (
    widgetInit: NonNullable<typeof window.smartbotWidgetInit>,
    params: WidgetInitParams,
    parentSignal: AbortSignal
  ): Promise<boolean> => {
    if (parentSignal.aborted) {
      return false;
    }

    // Widget API сообщает о результате через callbacks, а store работает с
    // Promise. Этот адаптер связывает внешний AbortSignal, timeout и callbacks.
    const initAbortController = new AbortController();
    const abortInit = (): void => initAbortController.abort();
    parentSignal.addEventListener('abort', abortInit, { once: true });

    const initialized = await new Promise<boolean>((resolve) => {
      let settled = false;

      const settle = (result: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        initAbortController.signal.removeEventListener('abort', handleAbort);
        resolve(result);
      };

      const handleAbort = (): void => {
        window.clearTimeout(timeoutId);
        settle(false);
      };

      const timeoutId = window.setTimeout(
        () => initAbortController.abort(),
        WIDGET_INIT_TIMEOUT_MS
      );

      initAbortController.signal.addEventListener('abort', handleAbort, {
        once: true
      });

      const handleSuccess = (): void => {
        window.clearTimeout(timeoutId);
        settle(true);
      };

      const handleError = (): void => {
        window.clearTimeout(timeoutId);
        settle(false);
      };

      void widgetInit(params, handleSuccess, handleError, {
        signal: initAbortController.signal
      }).catch(handleError);
    });

    parentSignal.removeEventListener('abort', abortInit);
    return initialized;
  };
}

export default SmartbotSupportWidgetStore;
